import type { ApiErrorBody } from '@neyara/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Field errors from zod: { fieldName: ['message'] } */
    readonly fields: Record<string, string[] | undefined> = {},
  ) {
    super(message);
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

export function withQuery(path: string, query?: Query) {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined && { 'Content-Type': 'application/json' }),
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (res.status === 204) return undefined as T;

  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (body ?? {}) as Partial<ApiErrorBody>;
    const fields = typeof err.details === 'object' && err.details !== null ? (err.details as ApiError['fields']) : {};
    throw new ApiError(res.status, err.error ?? `Request failed (${res.status})`, fields);
  }
  return body as T;
}
